/**
 * /igaming/payment-hold — compliance payment-hold operator page (Feature #157).
 *
 * Declares a TanStack Router route and renders `IgamingPaymentHoldPage`.
 * The component is imported by the root-owned `router.tsx` via module
 * augmentation — agents never touch `router.tsx` themselves; the root
 * wire-up cycle picks this route up after the prod merge.
 *
 * Search params: `?hold=ighld_xxx` deep-links to an individual hold's
 * detail drawer.
 */
import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { IgamingPaymentHoldPage } from "@/features/igaming-payment-hold/IgamingPaymentHoldPage";
import { isValidId } from "@/features/igaming-payment-hold/api";

// ---------------------------------------------------------------------------
// Route — mounts at `/igaming/payment-hold`.
// ---------------------------------------------------------------------------

interface IgamingPaymentHoldSearch {
	hold?: string;
}

export const igamingPaymentHoldRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/igaming/payment-hold",
	validateSearch: (raw: Record<string, unknown>): IgamingPaymentHoldSearch => {
		const hold = raw.hold;
		if (isValidId(hold)) return { hold };
		return {};
	},
	component: IgamingPaymentHoldRouteView,
});

export default igamingPaymentHoldRoute;

// ---------------------------------------------------------------------------
// Page shell — static breadcrumb + the live feature component.
// ---------------------------------------------------------------------------

export function IgamingPaymentHoldRouteView() {
	const { hold } = igamingPaymentHoldRoute.useSearch();

	return (
		<div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<IgamingPaymentHoldPage initialHoldId={hold} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">iGaming</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Payment hold</li>
			</ol>
		</nav>
	);
}
